import { useCallback, useEffect, useRef, useState } from "react";
import { ackQaIssue, listQaIssues, type QaIssue, type QaIssuesResponse } from "../api/qaIssues";

type Props = { project: string; active: boolean };

const SEV_LABEL: Record<QaIssue["severity"], string> = { high: "high", medium: "medium", low: "low" };
const CAT_LABEL: Record<QaIssue["category"], string> = {
  visual: "visual",
  static: "static",
  functional: "behavior",
  runtime: "runtime",
};

type Filter = "all" | "open" | "fixed";
const FILTER_ORDER: Filter[] = ["all", "open", "fixed"];
const FILTER_LABEL: Record<Filter, string> = { all: "All", open: "Open", fixed: "Fixed" };

/** Open = needs action: no ack, or disputed (re-broke after a fix). */
function isOpenBucket(i: QaIssue): boolean {
  return !i.ack || i.ack.status === "disputed";
}
/** Dev marked it fixed but QA has not re-verified yet (still in the open set). */
function isFixedPending(i: QaIssue): boolean {
  return i.ack?.status === "fixed";
}

function fmtAgo(iso: string): string {
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return "";
  const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function QaIssueCard({
  issue,
  project,
  refresh,
}: {
  issue: QaIssue;
  project: string;
  refresh: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [ackError, setAckError] = useState<string | null>(null);
  const ack = issue.ack;

  const act = useCallback(
    async (action: "fix" | "cancel") => {
      setBusy(true);
      setAckError(null);
      try {
        await ackQaIssue(project, issue.signature, action);
        await refresh();
      } catch (e) {
        setAckError((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [project, issue.signature, refresh],
  );

  return (
    <li className={`qa-issue qa-sev-${issue.severity}`}>
      <div className="qa-issue-head">
        <span className={`qa-badge qa-badge-${issue.severity}`}>{SEV_LABEL[issue.severity]}</span>
        <span className="qa-cat">{CAT_LABEL[issue.category]}</span>
        <span className="qa-issue-title">{issue.title}</span>
        {issue.provisional && <span className="qa-provisional" title="found mid-run, still verifying">checking…</span>}
        <span className="qa-issue-age">{fmtAgo(issue.lastSeenAt)}</span>
      </div>
      {issue.contentId && <div className="qa-issue-id"><code>{issue.contentId}</code></div>}
      {issue.detail && <div className="qa-issue-detail">{issue.detail}</div>}
      {issue.shotUrls.length > 0 && (
        <div className="qa-shots">
          {[...issue.shotUrls].reverse().map((u, idx) => (
            // Newest shot first; eager-load the first few so the latest evidence
            // appears immediately instead of lazy-loading oldest-first.
            <a key={u} href={u} target="_blank" rel="noopener noreferrer">
              <img src={u} alt={issue.title} decoding="async" loading={idx < 4 ? "eager" : "lazy"} />
            </a>
          ))}
        </div>
      )}

      <div className="qa-ack">
        {issue.resolvedAt ? (
          <div className="qa-ack-fixed">
            <span className="qa-ack-fixed-msg">
              ✓ Resolved · verified clean
              {issue.ack ? <> · originally fixed by <strong>{issue.ack.devName}</strong></> : null}
              {" · "}{fmtAgo(issue.resolvedAt)}
            </span>
          </div>
        ) : ack?.status === "disputed" ? (
          <div className="qa-ack-disputed">
            <p className="qa-ack-disputed-msg">
              ⚠ Emil flagged this again after it was marked fixed by{" "}
              <strong>{ack.devName}</strong> ({fmtAgo(ack.fixedAt)}
              {ack.disputedAt ? `, re-detected ${fmtAgo(ack.disputedAt)}` : ""}). Confirm it is fixed?
            </p>
            <div className="qa-ack-actions">
              <button type="button" className="qa-ack-btn qa-ack-btn-confirm" disabled={busy} onClick={() => void act("fix")}>
                Confirm fixed
              </button>
              <button type="button" className="qa-ack-btn qa-ack-btn-cancel" disabled={busy} onClick={() => void act("cancel")}>
                Cancel
              </button>
            </div>
          </div>
        ) : ack?.status === "fixed" ? (
          <div className="qa-ack-fixed">
            <span className="qa-ack-fixed-msg">
              ✓ Fixed by <strong>{ack.devName}</strong> · {fmtAgo(ack.fixedAt)}
            </span>
            <button type="button" className="qa-ack-btn qa-ack-btn-cancel" disabled={busy} onClick={() => void act("cancel")}>
              Cancel
            </button>
          </div>
        ) : (
          <button type="button" className="qa-ack-btn qa-ack-btn-fix" disabled={busy} onClick={() => void act("fix")}>
            Mark as fixed
          </button>
        )}
        {ackError && <span className="qa-ack-error" role="alert">{ackError}</span>}
      </div>
    </li>
  );
}

export default function QaIssuesPanel({ project, active }: Props) {
  const [data, setData] = useState<QaIssuesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [filter, setFilter] = useState<Filter>("open");

  const refresh = useCallback(async () => {
    try {
      setData(await listQaIssues(project));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [project]);

  // Poll while the tab is open so issues appear/clear live DURING a QA run.
  useEffect(() => {
    if (!active) return;
    void refresh();
    timer.current = setInterval(() => void refresh(), 5000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [active, refresh]);

  if (error) {
    return (
      <div className="qa-panel">
        <div className="error-banner" role="alert">
          <span>Could not load QA issues: {error}</span>
          <button type="button" onClick={() => void refresh()}>Retry</button>
        </div>
      </div>
    );
  }

  if (data === null) return <div className="qa-panel"><p className="placeholder">Loading QA issues…</p></div>;

  if (!data.configured) {
    return (
      <div className="qa-panel">
        <p className="placeholder">Emil is not connected to this dashboard yet.</p>
      </div>
    );
  }

  if (data.error || typeof data.upstream === "number") {
    return (
      <div className="qa-panel">
        <p className="placeholder">QA results are unavailable right now. Retrying automatically…</p>
      </div>
    );
  }

  const open = data.issues.filter(isOpenBucket);
  const fixedPending = data.issues.filter(isFixedPending);
  // Defense-in-depth: the qa-agent guarantees a signature lives in exactly one of
  // issues/resolved, but the two services deploy independently, so we drop any
  // resolved entry that also appears in the open set to keep counts + React keys correct.
  const openSigs = new Set(data.issues.map((i) => i.signature));
  const resolved = (data.resolved ?? []).filter((r) => !openSigs.has(r.signature));
  const fixed = [...fixedPending, ...resolved]; // dev-claimed (active) first, then verified-clean
  const counts: Record<Filter, number> = { all: open.length + fixed.length, open: open.length, fixed: fixed.length };
  const shown = (filter === "open" ? open : filter === "fixed" ? fixed : [...open, ...fixed])
    .slice()
    .sort((a, b) => (Date.parse(b.lastSeenAt) || 0) - (Date.parse(a.lastSeenAt) || 0)); // newest first
  const provisional = data.issues.some((i) => i.provisional);

  return (
    <div className="qa-panel">
      <div className="qa-panel-head">
        <div className="qa-filters" role="tablist" aria-label="Filter QA issues">
          {FILTER_ORDER.map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={filter === f}
              className={`qa-filter${filter === f ? " qa-filter-active" : ""}`}
              onClick={() => setFilter(f)}
            >
              {FILTER_LABEL[f]} <span className="qa-filter-count">{counts[f]}</span>
            </button>
          ))}
        </div>
        {provisional && (
          <span className="qa-running" aria-live="polite">
            <span className="pulse" aria-hidden="true" /> QA running — updating live
          </span>
        )}
        {data.updatedAt && <span className="qa-updated">checked {fmtAgo(data.updatedAt)}</span>}
      </div>

      {shown.length === 0 ? (
        <p className="placeholder qa-clear">
          {filter === "open"
            ? "No open issues for this build. Emil found nothing wrong, or every earlier issue has been fixed and re-verified."
            : filter === "fixed"
              ? "No fixed issues yet for this build."
              : "Nothing to show for this build yet."}
        </p>
      ) : (
        <ul className="qa-issue-list">
          {shown.map((i) => (
            <QaIssueCard key={i.signature} issue={i} project={project} refresh={refresh} />
          ))}
        </ul>
      )}
    </div>
  );
}
